import { useRef, useState, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FileRow } from "./FileRow";
import { FolderRow } from "./FolderRow";
import type { ExplorerItem, FileItem } from "@titancloud/shared";
import { ContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { PreviewModal } from "../preview/PreviewModal";
import { downloadFile } from "../../api/files.api";
import { downloadFolderZip } from "../../api/folders.api";
import { downloadAdminExplorerFile } from "../../api/admin.api";

interface VirtualFileListProps {
  items: ExplorerItem[];
  onRename: (id: string, kind: "file" | "folder", name: string) => void;
  onDelete: (id: string, kind: "file" | "folder", name: string) => void;
  readOnly?: boolean;
  explorerUserId?: string;
  onFolderNavigate?: (folderId: string) => void;
  isCreatingFolder?: boolean;
  creatingFolderName?: string;
  onDownload?: (fileId: string, fileName: string) => void;
  onAdminErase?: (fileId: string, fileName: string) => void;
  /** When provided, the list fetches the next page itself as the user nears the bottom. */
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const LOAD_MORE_ROW_THRESHOLD = 10;

interface ContextMenuState {
  x: number;
  y: number;
  item: ExplorerItem;
}

export function VirtualFileList({
  items,
  onRename,
  onDelete,
  readOnly,
  explorerUserId,
  onFolderNavigate,
  isCreatingFolder = false,
  creatingFolderName = "",
  onDownload,
  onAdminErase,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
}: VirtualFileListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Only file-kind items can be previewed
  const fileItems = items.filter((i): i is FileItem => i.kind === "file");

  const openPreview = (fileId: string) => {
    const idx = fileItems.findIndex((f) => f.id === fileId);
    if (idx !== -1) setPreviewIndex(idx);
  };

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 10,
  });

  // Infinite scroll: fetch the next page once the last rendered row nears the bottom.
  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
    const lastItem = virtualItems[virtualItems.length - 1];
    if (lastItem && lastItem.index >= items.length - 1 - LOAD_MORE_ROW_THRESHOLD) {
      onLoadMore();
    }
  }, [virtualItems, items.length, hasNextPage, isFetchingNextPage, onLoadMore]);

  const handleContextMenu = (e: React.MouseEvent, item: ExplorerItem) => {
    e.preventDefault();
    if (readOnly && item.kind === "folder") return;
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  const downloadIcon = (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
      <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z" />
    </svg>
  );

  const eraseIcon = (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
      <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 110-2h3.5l1-1h3l1 1h3.5a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118z" clipRule="evenodd" />
    </svg>
  );

  const getContextMenuItems = (item: ExplorerItem): ContextMenuItem[] => {
    if (readOnly && explorerUserId && item.kind === "file") {
      const menuItems: ContextMenuItem[] = [
        {
          label: "Download",
          onClick: () => onDownload
            ? onDownload(item.id, item.name)
            : void downloadAdminExplorerFile(explorerUserId, item.id, item.name),
          icon: downloadIcon,
        },
      ];
      if (onAdminErase) {
        menuItems.push({
          label: "Erase from S3",
          onClick: () => onAdminErase(item.id, item.name),
          danger: true,
          divider: true,
          icon: eraseIcon,
        });
      }
      return menuItems;
    }
    return [
      {
        label: "Rename",
        onClick: () => setRenameId(item.id),
        icon: (
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
      },
      {
        label: item.kind === "folder" ? "Download as ZIP" : "Download",
        onClick: () => {
          if (item.kind === "folder") {
            downloadFolderZip(item.id, item.name);
          } else {
            void downloadFile(item.id, item.name);
          }
        },
        icon: downloadIcon,
      },
      {
        label: "Delete",
        onClick: () => onDelete(item.id, item.kind, item.name),
        danger: true,
        divider: true,
        icon: (
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
            <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 110-2h3.5l1-1h3l1 1h3.5a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118z" clipRule="evenodd" />
          </svg>
        ),
      },
    ];
  };

  return (
    <>
      {/* Header row — structure mirrors FileRow/FolderRow: gap-3, checkbox placeholder, icon placeholder, then columns */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-2 text-xs font-medium text-gray-500">
        <div className="h-4 w-4 flex-shrink-0" /> {/* checkbox placeholder */}
        <div className="h-5 w-5 flex-shrink-0" /> {/* icon placeholder */}
        <div className="min-w-0 flex-1">Name</div>
        <div className="hidden w-24 flex-shrink-0 text-right sm:block">Size</div>
        <div className="hidden w-36 flex-shrink-0 text-right sm:block">Modified</div>
        <div className="w-8 flex-shrink-0" />
      </div>

      {/* Ghost row — shown while a folder is being created */}
      {isCreatingFolder && (
        <div className="flex animate-pulse items-center gap-3 border-b border-gray-800/50 bg-gray-900/40 px-4 py-3">
          <div className="h-4 w-4 flex-shrink-0" />
          <svg className="h-5 w-5 flex-shrink-0 text-yellow-400/50" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          <div className="min-w-0 flex-1">
            {creatingFolderName ? (
              <span className="text-sm text-gray-500">{creatingFolderName}</span>
            ) : (
              <div className="h-3 w-32 rounded bg-gray-700" />
            )}
          </div>
          <div className="hidden w-24 flex-shrink-0 sm:block" />
          <div className="hidden w-36 flex-shrink-0 sm:block" />
          <div className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-gray-600 border-t-gray-400 animate-spin" />
        </div>
      )}

      <div ref={parentRef} className="flex-1 overflow-auto">
        <div
          style={{ height: virtualizer.getTotalSize() }}
          className="relative"
        >
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            if (!item) return null;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualItem.start}px)` }}
                onContextMenu={(e) => handleContextMenu(e, item)}
              >
                {item.kind === "folder" ? (
                  <FolderRow
                    folder={item}
                    isRenaming={!readOnly && renameId === item.id}
                    onRenameComplete={(name) => {
                      onRename(item.id, "folder", name);
                      setRenameId(null);
                    }}
                    onRenameCancel={() => setRenameId(null)}
                    {...(readOnly ? { readOnly: true as const } : {})}
                    {...(onFolderNavigate ? { onFolderNavigate } : {})}
                  />
                ) : (
                  <FileRow
                    file={item}
                    isRenaming={!readOnly && renameId === item.id}
                    onRenameComplete={(name) => {
                      onRename(item.id, "file", name);
                      setRenameId(null);
                    }}
                    onRenameCancel={() => setRenameId(null)}
                    onOpen={() => openPreview(item.id)}
                    {...(readOnly ? { readOnly: true as const } : {})}
                    {...(explorerUserId ? { explorerUserId } : {})}
                  />
                )}
              </div>
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-500">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-gray-600 border-t-gray-400 animate-spin" />
            Loading more…
          </div>
        )}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems(contextMenu.item)}
          onClose={() => setContextMenu(null)}
        />
      )}

      {previewIndex !== null && fileItems.length > 0 && (
        <PreviewModal
          files={fileItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          hasMore={hasNextPage}
          isLoadingMore={isFetchingNextPage}
          {...(onLoadMore ? { onLoadMore } : {})}
          {...(explorerUserId ? { explorerUserId } : {})}
        />
      )}
    </>
  );
}
